/**
 * Human approval presentation (prompt.md §25, §23).
 *
 * WHY the arguments are formatted here rather than in the component: a person
 * approving a destructive action must see exactly what the agent is about to do
 * — the same tool id the platform will execute and the same argument values —
 * with no summarising, no paraphrasing and nothing hidden behind a "details"
 * toggle by default. Tests assert the exact values survive into the description.
 */

import type { Approval } from "@dayvise/domain";
import { formatArguments, toolLabel } from "./messageParts.ts";

export interface ApprovalArgumentRow {
  readonly name: string;
  readonly value: string;
}

export interface ApprovalDescriptor {
  /** e.g. `GitHub Create Pull Request`. */
  readonly toolLabel: string;
  readonly toolId: string;
  readonly headline: string;
  readonly riskLabel: string;
  readonly tone: "neutral" | "accent" | "success" | "warning" | "danger";
  /** Ordered argument rows; the full, untruncated value of each argument. */
  readonly arguments: readonly ApprovalArgumentRow[];
  /** Pretty-printed arguments, for the raw JSON view and for copy/paste. */
  readonly argumentsText: string;
  /** True when the action is destructive enough to need a deliberate confirmation. */
  readonly requiresExplicitConfirmation: boolean;
  /** The action request that will be executed verbatim if approved. */
  readonly actionLine: string;
}

const RISK_LABELS: Readonly<Record<string, string>> = {
  low: "Low risk",
  medium: "Needs review",
  high: "High risk",
  critical: "Critical — destructive",
};

const RISK_TONES: Readonly<Record<string, ApprovalDescriptor["tone"]>> = {
  low: "neutral",
  medium: "accent",
  high: "warning",
  critical: "danger",
};

/** Spread top-level object arguments into rows so most approvals are readable. */
function argumentRows(args: unknown): ApprovalArgumentRow[] {
  if (args === null || args === undefined) return [];
  if (typeof args !== "object" || Array.isArray(args)) {
    return [{ name: "arguments", value: formatArguments(args, Number.MAX_SAFE_INTEGER) }];
  }
  return Object.entries(args as Record<string, unknown>).map(([name, value]) => ({
    name,
    value: typeof value === "string" ? value : formatArguments(value, Number.MAX_SAFE_INTEGER),
  }));
}

export function describeApproval(approval: Approval): ApprovalDescriptor {
  const label = toolLabel(approval.toolId);
  const riskLabel = RISK_LABELS[approval.riskLevel] ?? approval.riskLevel;
  const requiresExplicitConfirmation = approval.riskLevel === "critical" || approval.riskLevel === "high";
  return {
    toolLabel: label,
    toolId: approval.toolId,
    headline: `Approve ${label}?`,
    riskLabel,
    tone: RISK_TONES[approval.riskLevel] ?? "accent",
    arguments: argumentRows(approval.arguments),
    argumentsText: formatArguments(approval.arguments, Number.MAX_SAFE_INTEGER),
    requiresExplicitConfirmation,
    actionLine: `${approval.toolId}(${JSON.stringify(approval.arguments ?? {})})`,
  };
}

/** Status label for an approval record, for history views. */
export function approvalStatusLabel(status: Approval["status"]): string {
  switch (status) {
    case "pending":
      return "Awaiting decision";
    case "approved":
      return "Approved";
    case "rejected":
      return "Rejected";
    default:
      return "Expired";
  }
}
